import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import LocationPicker from "../components/LocationPicker";
import VoiceRecorder from "../components/VoiceRecorder";
import LocationCandidatesModal from "../components/LocationCandidatesModal";
import api from "../lib/api";
import { compressImage } from "../lib/format";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Camera, Wrench, Package, Users, Sparkle } from "@phosphor-icons/react";

export default function MaintenanceNew() {
  const { session } = useAuth();
  const nav = useNavigate();
  const [location, setLocation] = useState(null);
  const [severity, setSeverity] = useState("");
  const [note, setNote] = useState("");
  const [route, setRoute] = useState("interna");
  const [cost, setCost] = useState(0);
  const [supplierId, setSupplierId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientToken] = useState(() => crypto.randomUUID());
  const [voiceCandidates, setVoiceCandidates] = useState([]);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [showCandidates, setShowCandidates] = useState(false);
  const isProtected = session?.hotel?.license_status === "PROTEGIDA";

  React.useEffect(() => { api.get("/suppliers").then(r => setSuppliers(r.data)); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
  };

  const handleVoiceResult = (res) => {
    if (!res) return;
    if (res.severity) setSeverity(res.severity);
    if (res.note) setNote(res.note);
    setVoiceTranscript(res.transcript || "");
    const cands = res.candidates || [];
    if (cands.length === 1 && (cands[0].confidence ?? 0) >= 0.75 && !cands[0].uncatalogued) {
      setLocation({ ...cands[0] });
    } else if (cands.length >= 1) {
      setVoiceCandidates(cands);
      setShowCandidates(true);
    } else if (res.transcript) {
      toast.info("Nota transcrita · elige la ubicación manualmente");
    }
  };

  const submit = async () => {
    if (loading) return;
    if (!location || !severity) { toast.error("Elige ubicación y severidad"); return; }
    setLoading(true);
    try {
      const payload = {
        room: location.value, severity, note,
        asset_id: location.asset_id || null,
        asset_hint: location.kind === "activo" ? location.value : null,
        uncatalogued: !!location.uncatalogued,
        route, estimated_cost: parseFloat(cost) || 0,
        supplier_id: supplierId || null, client_token: clientToken,
      };
      const { data: rep } = await api.post("/reports/tech", payload);
      if (file) {
        try {
          const compressed = await compressImage(file);
          const fd = new FormData(); fd.append("file", compressed);
          await api.post(`/reports/${rep.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { toast.warning("Reporte enviado, foto no subió."); nav("/mantenimiento"); return; }
      }
      toast.success("Reporte técnico enviado · pasa directo a aprobación");
      nav("/mantenimiento");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error de red al enviar.");
    } finally { setLoading(false); }
  };

  if (isProtected) {
    return <AppShell title="Reportar como técnico">
      <div className="q-card p-6 text-center text-[color:var(--q-muted)]">Licencia PROTEGIDA · no se pueden crear reportes nuevos.</div>
    </AppShell>;
  }

  return (
    <AppShell title="Reportar como técnico">
      <div className="grid gap-6 max-w-2xl">
        <div>
          <div className="q-label mb-3 flex items-center gap-2">
            <Sparkle size={12} weight="fill" className="text-[color:var(--q-gold)]"/>
            Nota de voz (opcional)
          </div>
          <VoiceRecorder onResult={handleVoiceResult}/>
        </div>
        <div>
          <div className="q-label mb-3">1 · Ubicación</div>
          <LocationPicker value={location} onChange={setLocation}/>
        </div>
        <div>
          <div className="q-label mb-3">2 · Foto</div>
          <label htmlFor="tech-photo" className="q-camera-label">
            {preview ? <img src={preview} alt="preview" className="max-h-48 rounded-lg"/> : (
              <><Camera size={44} weight="fill"/><div className="mt-3 font-semibold">Tomar foto</div></>
            )}
          </label>
          <input id="tech-photo" data-testid="tech-photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile}/>
        </div>
        <div>
          <div className="q-label mb-3">3 · Severidad</div>
          <div className="grid grid-cols-3 gap-2">
            {[{v:"leve",label:"Leve"},{v:"media",label:"Media"},{v:"grave",label:"Grave"}].map(s => (
              <button key={s.v} data-testid={`tech-sev-${s.v}`} onClick={() => setSeverity(s.v)}
                className={`p-4 rounded-lg border font-bold ${severity === s.v ? `chip-${s.v} border-current !bg-opacity-100` : "bg-white border-[color:var(--q-border)]"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="q-label mb-3">4 · Clasificación técnica (va directo a Gerencia)</div>
          <div className="grid grid-cols-3 gap-2">
            {[{v:"interna",label:"Reparación interna",Ic:Wrench},
              {v:"repuesto",label:"Requiere repuesto",Ic:Package},
              {v:"contratista",label:"Contratista",Ic:Users}].map(o => {
              const OIc = o.Ic;
              return (
                <button key={o.v} data-testid={`tech-route-${o.v}`} onClick={() => setRoute(o.v)}
                  className={`p-3 rounded-lg border text-left ${route === o.v ? `badge-${o.v}` : "bg-white border-[color:var(--q-border)]"}`}>
                  <OIc size={18} weight="fill"/>
                  <div className="text-xs font-semibold mt-1">{o.label}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="q-label mb-2">Costo estimado (USD)</div>
            <input data-testid="tech-cost" value={cost} onChange={(e) => setCost(e.target.value)} type="number"
              className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
          </div>
          {route !== "interna" && (
            <div>
              <div className="q-label mb-2">Proveedor</div>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="tech-supplier"
                className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]">
                <option value="">Sin asignar</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name} · {s.category}</option>)}
              </select>
            </div>
          )}
        </div>
        <div>
          <div className="q-label mb-2">Nota (opcional)</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="tech-note-input"
            placeholder="Diagnóstico rápido"
            className="w-full h-20 p-3 rounded-lg border border-[color:var(--q-border)] text-sm"/>
        </div>
        <button data-testid="tech-submit" onClick={submit} disabled={loading || !location || !severity} className="q-btn-primary h-14 text-lg">
          {loading ? "Enviando…" : "Enviar a Gerencia"}
        </button>
      </div>
      <LocationCandidatesModal
        open={showCandidates}
        candidates={voiceCandidates}
        transcript={voiceTranscript}
        onPick={(c) => { setLocation(c); setShowCandidates(false); }}
        onClose={() => setShowCandidates(false)}
      />
    </AppShell>
  );
}
